import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose, MdOutlineUploadFile } from 'react-icons/md';
import { toast } from 'react-toastify';
import Modal from '../layouts/Modal';
import { Photo } from '../../types/photoAlbum';
import Button from '../misc/Button';

interface AddPhotosModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpload: (files: File[]) => Promise<Photo[]>;
  isUploading?: boolean;
}

export default function AddPhotosModal({
  open,
  setOpen,
  onUpload,
  isUploading: externalUploading = false,
}: AddPhotosModalProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        if (file.size <= 20 * 1024 * 1024) {
          // 20MB limit
          validFiles.push(file);
          newPreviews.push(URL.createObjectURL(file));
        } else {
          toast.error(t('component.modal.addPhotos.uploadTooLarge'));
        }
      } else {
        toast.error(t('component.modal.addPhotos.invalidFileType'));
      }
    });

    if (selectedPhotos.length + validFiles.length > 100) {
      toast.error(t('component.modal.addPhotos.tooManyFiles'));
      return;
    }

    setSelectedPhotos([...selectedPhotos, ...validFiles]);
    setPreviews([...previews, ...newPreviews]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removePhoto = (index: number) => {
    const newPhotos = selectedPhotos.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    URL.revokeObjectURL(previews[index]);

    setSelectedPhotos(newPhotos);
    setPreviews(newPreviews);
  };

  const handleUpload = async () => {
    if (selectedPhotos.length === 0) return;

    setIsUploading(true);
    try {
      await onUpload(selectedPhotos);

      // Clean up previews
      previews.forEach((preview) => URL.revokeObjectURL(preview));
      setSelectedPhotos([]);
      setPreviews([]);
      setOpen(false);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading || externalUploading) return;

    // Clean up previews
    previews.forEach((preview) => URL.revokeObjectURL(preview));
    setSelectedPhotos([]);
    setPreviews([]);
    setOpen(false);
  };

  const uploading = isUploading || externalUploading;

  return (
    <Modal open={open} setOpen={setOpen} title={t('component.modal.addPhotos.title')}>
      {/* Drop zone */}
      <div
        className="bg-secondary-100/50 hover:bg-secondary-100 mb-6 cursor-pointer rounded-xl border-2 border-dashed border-primary/30 p-8 transition-colors hover:border-primary"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleInputChange}
          className="hidden"
        />

        <div className="flex flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <MdOutlineUploadFile className="h-8 w-8 text-primary" />
          </div>
          <h4 className="mb-2 text-lg font-semibold text-primary">{t('component.modal.addPhotos.uploadArea')}</h4>
          <p className="mb-4 text-sm text-details/60">{t('component.modal.addPhotos.uploadInstructions')}</p>
          <Button variant="secondary">{t('component.modal.addPhotos.selectFiles')}</Button>
        </div>
      </div>

      {/* Selected photos preview */}
      {selectedPhotos.length > 0 && (
        <div className="mb-6">
          <h4 className="mb-3 font-medium text-primary">
            {t('component.modal.addPhotos.selectedPhotos', { count: selectedPhotos.length })}
          </h4>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {previews.map((preview, index) => {
              const file = selectedPhotos[index];
              const isVideo = file.type.startsWith('video/');

              return (
                <div key={index} className="bg-secondary-100 group relative aspect-square overflow-hidden rounded-lg">
                  {isVideo ? (
                    <video src={preview} className="h-full w-full object-cover" muted preload="metadata" />
                  ) : (
                    <img src={preview} alt={`Preview ${index + 1}`} className="h-full w-full object-cover" />
                  )}

                  {/* Video indicator */}
                  {isVideo && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="rounded-full bg-black/60 p-2">
                        <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePhoto(index);
                    }}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <MdClose className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-3">
        <Button variant="tertiary" onClick={handleClose} disabled={uploading}>
          {t('general.cancel')}
        </Button>
        <Button variant="primary" onClick={handleUpload} disabled={selectedPhotos.length === 0 || uploading}>
          {uploading
            ? t('page.dashboard.albums.uploading')
            : t('component.modal.addPhotos.uploadPhotos', { count: selectedPhotos.length })}
        </Button>
      </div>
    </Modal>
  );
}
